import { XMarkIcon } from '@heroicons/react/24/outline';

import { Tooltip } from '@/components/Tooltip';
import { Button } from '@/components/ui/button';
import { StatusChip } from '@/components/ui/status-chip';
import { parseProgress } from '@/hooks/useBulkJobs/parseProgress';
import { type BulkJobRecord } from '@/state/bulkJobs.store';
import { formatDate } from '@/utils/date.util';

import { humanizeAction } from './actionLabels';
import { BulkEntityIconCircle } from './BulkEntityIconCircle';

function statusVariant(
  status: BulkJobRecord['status']
): 'muted' | 'high' | 'info' | 'critical' | 'default' {
  switch (status) {
    case 'queued':
      return 'muted';
    case 'running':
      return 'high';
    case 'pass':
      return 'info';
    case 'fail':
      return 'critical';
    case 'partial':
      return 'high';
  }
}

function statusLabel(status: BulkJobRecord['status']): string {
  switch (status) {
    case 'queued':
      return 'Queued';
    case 'running':
      return 'Running';
    case 'pass':
      return 'Completed';
    case 'fail':
      return 'Failed';
    case 'partial':
      return 'Partial';
  }
}

interface BulkJobRowProps {
  job: BulkJobRecord;
  onDismiss: (jobId: string) => void;
  /** Injected render timestamp for testability. Defaults to job.submittedAt-based display. */
  nowMs?: number;
}

export function BulkJobRow({ job, onDismiss, nowMs }: BulkJobRowProps) {
  const progress = parseProgress(job.comment);
  const isActive = job.status === 'queued' || job.status === 'running';

  // nowMs is passed in by caller (avoids impure Date.now() during render per
  // React compiler rules). Clamp so a stale/frozen nowMs can never render
  // future phrasing like "in 35 minutes" (ENG-5133).
  const effectiveNowMs = Math.max(nowMs ?? job.submittedAt, job.submittedAt);

  // In-flight rows anchor to submittedAt and tick a live relative age
  // (ENG-5133). Terminal rows anchor to completedAt and freeze: a finished
  // job's age climbing every second while the drawer stays open is noise —
  // the user cares *when* it finished, not how long ago (ENG-5184). Fall back
  // to submittedAt for any legacy record stamped before completedAt existed.
  const anchorMs = isActive
    ? job.submittedAt
    : (job.completedAt ?? job.submittedAt);
  // RFC3339 without milliseconds — same shape the asset table's First/Last
  // Seen columns receive from the backend and show in their tooltips.
  const anchorIso = new Date(anchorMs).toISOString().replace(/\.\d{3}/, '');

  // In-flight: coarsened relative time ("now" / "5 minutes ago" / absolute
  // date past 30 days) against the live clock, with the explicit `now`
  // argument keeping formatDate pure under the React Compiler. Terminal: a
  // frozen absolute completion time (date + local time-of-day) that never
  // climbs, so no `useNow` tick is consumed.
  const timeLabel = isActive
    ? formatDate(anchorIso, undefined, true, new Date(effectiveNowMs))
    : formatDate(anchorIso, 'MMM d, h:mm a', false);

  return (
    <div className="flex items-start gap-3 px-4 py-3">
      <BulkEntityIconCircle resource={job.resource} />

      <div className="min-w-0 flex-1">
        {/* Primary label */}
        <p className="text-chariot-text-primary text-sm font-medium">
          {humanizeAction(job.action)} {job.resource}s
        </p>

        {/* Progress bar when running */}
        {isActive && progress && (
          <div className="mt-1.5 space-y-0.5">
            {(() => {
              const percent =
                progress.total > 0
                  ? Math.round((progress.processed / progress.total) * 100)
                  : 0;
              return (
                <div
                  role="progressbar"
                  aria-valuenow={percent}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={`${humanizeAction(job.action)} progress: ${percent}%`}
                  className="bg-chariot-stroke h-1 w-full overflow-hidden rounded-full"
                >
                  <div
                    className="bg-chariot-brand-primary h-1 rounded-full transition-all duration-500"
                    style={{ width: `${percent}%` }}
                  />
                </div>
              );
            })()}
            <p className="text-chariot-text-secondary text-xs">
              {progress.processed}/{progress.total}
              {progress.failed > 0 && (
                <span className="text-chariot-status-critical ml-1">
                  ({progress.failed} failed)
                </span>
              )}
            </p>
          </div>
        )}

        {/* Status + timestamp */}
        <div className="mt-1 flex items-center gap-2">
          <StatusChip
            label={statusLabel(job.status)}
            variant={statusVariant(job.status)}
          />
          <Tooltip title={anchorIso} placement="bottom-end">
            {/* tabIndex makes the Radix tooltip keyboard-reachable: it opens
                on focus, exposing the exact timestamp to keyboard-only users */}
            <span
              tabIndex={0}
              className="text-chariot-text-secondary focus-visible:ring-ring rounded text-xs focus-visible:ring-1 focus-visible:outline-hidden"
            >
              {timeLabel}
            </span>
          </Tooltip>
        </div>
      </div>

      {/* Reserved cancel slot (w-16) + dismiss button */}
      <div className="flex shrink-0 items-center gap-1">
        {/* w-16 reserved for future cancel affordance (per plan §2.6) */}
        <div className="w-0" aria-hidden="true" />
        {!isActive && (
          <Button
            variant="ghost"
            size="icon"
            className="size-6"
            onClick={() => onDismiss(job.jobId)}
            aria-label={`Dismiss ${humanizeAction(job.action)} ${job.resource}s job`}
          >
            <XMarkIcon className="size-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}
